/**
 * @file Left-click-drag camera yaw — the mouse equivalent of holding Q/E.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHICH WAY A DRAG TURNS
 * ─────────────────────────────────────────────────────────────────────────────
 * Dragging RIGHT sends the world right under the cursor, which means the camera
 * orbits the other way — so the yaw delta is `-dx`. That is what `OrbitControls`
 * does and what every 3D application does, and getting it backwards is the
 * single most common complaint about a drag-to-rotate camera. `invert: true`
 * flips it for anyone who wants "drag the camera" instead of "drag the world".
 *
 * The rate is radians PER PIXEL and deliberately not a fraction of the viewport:
 * a fullscreen player and a windowed one should get the same turn out of the
 * same wrist movement. `Math.PI / 480` puts a half-turn in a 480 px drag, which
 * lands close to Q/E's 115 deg/s for a normal-speed sweep.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY IT ACCUMULATES INSTEAD OF DRIVING THE RIG DIRECTLY
 * ─────────────────────────────────────────────────────────────────────────────
 * Pointer events land asynchronously and the sim is fixed-rate — a browser can
 * deliver five `pointermove`s between two steps, or none. Turning the rig from
 * the event handler would move the yaw AFTER the renderers had already synced
 * for that frame, so sprite rows and the targeting arc would be reading a yaw
 * the frame was not drawn at. Instead the deltas pile up in a scalar and the
 * step drains it with {@link DragYaw#consume} at exactly the point where Q/E is
 * read, which keeps the whole frame on one yaw (`systems/Gameplay.js`).
 *
 * Same lost-focus rule as `input/Keyboard.js`: an alt-tab mid-drag never
 * delivers `pointerup`, so a window blur ends the drag. Pointer capture keeps a
 * drag alive when the cursor leaves the canvas, which is most drags that matter.
 *
 * @module engine/input/DragYaw
 */

/** Radians of yaw per pixel of horizontal drag. A 480 px sweep is a half-turn. */
const YAW_PER_PIXEL = Math.PI / 480;

export class DragYaw {
  /**
   * @param {EventTarget|null} target The canvas. `null` is a valid, inert
   *   instance (node, and an engine built without a DOM).
   * @param {Object} [o]
   * @param {number} [o.button=0] `PointerEvent.button` that starts a drag. 0 is
   *   the left button; 2 would be the right one.
   * @param {number} [o.radPerPixel=Math.PI/480]
   * @param {boolean} [o.invert=false] Drag the CAMERA rather than the world.
   */
  constructor(target = null, o = {}) {
    const { button = 0, radPerPixel = YAW_PER_PIXEL, invert = false } = o;

    /** @type {EventTarget|null} */
    this.target = target ?? null;
    this.button = button;
    this.radPerPixel = radPerPixel;
    /** -1 = drag the world (the default), +1 = drag the camera. */
    this.sign = invert ? 1 : -1;
    /** @type {boolean} True between pointerdown and pointerup. */
    this.dragging = false;

    this._pointerId = -1;
    this._lastX = 0;
    /** Radians banked since the last {@link consume}. */
    this._accum = 0;
    this._disposed = false;

    this._onDown = (e) => this._handleDown(e);
    this._onMove = (e) => this._handleMove(e);
    this._onUp = (e) => this._handleUp(e);
    this._onBlur = () => this.cancel();

    const t = this.target;
    if (t?.addEventListener) {
      t.addEventListener('pointerdown', this._onDown);
      t.addEventListener('pointermove', this._onMove);
      t.addEventListener('pointerup', this._onUp);
      t.addEventListener('pointercancel', this._onUp);
    }
    // Safety net for the case pointer capture is unavailable and the button is
    // released somewhere else entirely.
    if (typeof window !== 'undefined' && window !== t) {
      window.addEventListener('pointerup', this._onUp);
      window.addEventListener('blur', this._onBlur);
    }
  }

  /**
   * Drains the banked rotation. Call it ONCE per fixed step, next to the Q/E
   * read — see the header.
   * @returns {number} Radians to hand to `CameraRig#rotateYawBy` (0 when idle).
   */
  consume() {
    const a = this._accum;
    this._accum = 0;
    return a;
  }

  /** Ends any drag in flight and throws away the banked rotation. */
  cancel() {
    this._end();
    this._accum = 0;
  }

  /** Idempotent. */
  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    this._end();
    const t = this.target;
    if (t?.removeEventListener) {
      t.removeEventListener('pointerdown', this._onDown);
      t.removeEventListener('pointermove', this._onMove);
      t.removeEventListener('pointerup', this._onUp);
      t.removeEventListener('pointercancel', this._onUp);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('pointerup', this._onUp);
      window.removeEventListener('blur', this._onBlur);
    }
    this._accum = 0;
    this.target = null;
  }

  // ── internals ──────────────────────────────────────────────────────────────

  /** @param {any} e */
  _handleDown(e) {
    if (this._disposed || this.dragging) return;
    if ((e?.button ?? 0) !== this.button) return;

    this.dragging = true;
    this._pointerId = e.pointerId ?? -1;
    this._lastX = e.clientX ?? 0;

    // Capture: a drag that leaves the canvas must keep turning, not stall at
    // the edge of the viewport.
    const t = /** @type {any} */ (this.target);
    if (this._pointerId >= 0 && typeof t?.setPointerCapture === 'function') {
      try {
        t.setPointerCapture(this._pointerId);
      } catch {
        // Some pointer types refuse capture; the window-level pointerup covers it.
      }
    }
    // Stops the browser turning the drag into a text/image selection.
    if (typeof e.preventDefault === 'function') e.preventDefault();
  }

  /** @param {any} e */
  _handleMove(e) {
    if (!this.dragging || this._disposed) return;
    // A second finger / a stray pen must not steer the drag we started.
    if (this._pointerId >= 0 && e?.pointerId !== undefined && e.pointerId !== this._pointerId) return;

    const x = e?.clientX ?? this._lastX;
    this._accum += (x - this._lastX) * this.radPerPixel * this.sign;
    this._lastX = x;
  }

  /** @param {any} e */
  _handleUp(e) {
    if (!this.dragging) return;
    if (this._pointerId >= 0 && e?.pointerId !== undefined && e.pointerId !== this._pointerId) return;
    this._end();
  }

  _end() {
    if (!this.dragging) return;
    const t = /** @type {any} */ (this.target);
    if (this._pointerId >= 0 && typeof t?.releasePointerCapture === 'function') {
      try {
        t.releasePointerCapture(this._pointerId);
      } catch {
        // Already released (the usual case on pointerup) — nothing to do.
      }
    }
    this.dragging = false;
    this._pointerId = -1;
  }
}

export { YAW_PER_PIXEL };
